import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Space, Typography, Spin, Result, Button } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { workOrdersApi } from './workOrdersApi';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Work Order "copy" sheet viewer (Stage WO-1A+, PDF since
// WO-COPY-PDF).
//
// The backend renders the A4 "WORK ORDER INFORMATION" sheet as a real,
// paginated PDF (GET /work-orders/:id/work-order-copy.pdf) via the generic
// Jasper engine. PDF gives EXACT, printer-safe page breaks — browser HTML
// printing re-paginated the flat HTML and cut rows across pages (the same
// problem the proforma print had before it moved to PDF). The PDF blob is
// embedded in an iframe (native PDF view); Print prints it directly and
// Download saves it. The template lives in the drop-in dir
// (report-templates/reports/wo_copy.jrxml), so its design/data can be
// iterated without a rebuild — click Reload to re-render.
// ─────────────────────────────────────────────────────────────────

async function errMessage(err) {
  const d = err && err.response && err.response.data;
  // With responseType:'blob', an error envelope arrives as a Blob — read it.
  if (d instanceof Blob) {
    try {
      const text = await d.text();
      try {
        const j = JSON.parse(text);
        if (j && j.error && j.error.message) return j.error.message;
      } catch { /* not JSON */ }
      if (text && text.length < 300) return text;
    } catch { /* ignore */ }
  }
  if (d && typeof d === 'object' && d.error && d.error.message) return d.error.message;
  return (err && err.message) || 'Could not load the work order copy.';
}

export default function WorkOrderCopyPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const urlRef = useRef(null);
  const wrapRef = useRef(null);

  const revoke = () => {
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null); revoke(); setUrl(null);
    try {
      const blob = await workOrdersApi.workOrderCopyPdf(id);
      const objUrl = URL.createObjectURL(blob);
      urlRef.current = objUrl;
      setUrl(objUrl);
    } catch (err) {
      setError(await errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load on mount / id change; revoke the object URL on unmount.
  useEffect(() => { load(); return revoke; }, [load]);

  const handlePrint = () => {
    const iframe = wrapRef.current && wrapRef.current.querySelector('iframe');
    if (iframe && iframe.contentWindow) { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
  };

  const handleDownload = () => {
    if (!urlRef.current) return;
    const a = document.createElement('a');
    a.href = urlRef.current;
    a.download = `work-order-${id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Card
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text strong>Work Order Copy</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            A4 PDF · work order information sheet · exact page breaks
          </Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Reload</Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload} disabled={!url}>Download</Button>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={!url}>Print</Button>
        </Space>
      }
      styles={{ body: { padding: 0 } }}
    >
      <div ref={wrapRef} style={{ height: 820, borderTop: '1px solid #f0f0f0' }}>
        {loading ? (
          <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="Rendering work order…" />
          </div>
        ) : error ? (
          <div style={{ padding: 24 }}>
            <Result
              status="error"
              title="Could not render the work order copy"
              subTitle={error}
              extra={
                <Space>
                  <Button onClick={() => navigate(-1)}>Back</Button>
                  <Button type="primary" onClick={load}>Retry</Button>
                </Space>
              }
            />
          </div>
        ) : (
          <iframe
            title="Work Order Copy"
            src={url || ''}
            style={{ width: '100%', height: '100%', border: 'none', background: '#f0f2f5' }}
          />
        )}
      </div>
    </Card>
  );
}
